// theme.ts (MUI v5)
import { createTheme } from "@mui/material/styles";

// Brand colors
const BROWN = "rgb(34, 35, 37)"; // #222325
const GOLD = "rgb(201, 176, 123)"; // #C9B07B

// Supporting neutrals
const OFF_WHITE = "#F7F7F5";
const BORDER = "rgba(255,255,255,0.10)";

const FROSTED_BACKGROUND = "rgba(255, 255, 255, 0.7)";
const FROSTED_BORDER = "1px solid rgba(255,255,255,0.25)";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: GOLD,
      contrastText: BROWN,
    },
    secondary: {
      main: BROWN,
      contrastText: "#FFFFFF",
    },
    background: {
      default: OFF_WHITE,
      paper: "#FFFFFF",
    },
    text: {
      primary: BROWN,
      secondary: "rgba(34, 35, 37, 0.70)",
    },
    divider: "rgba(34, 35, 37, 0.12)",
  },

  shape: {
    borderRadius: 12,
  },

  typography: {
    fontFamily: '"Inter", Arial, Helvetica, sans-serif',
    button: {
      textTransform: "none",
      fontWeight: 600,
      letterSpacing: 0.2,
    },
  },

  mixins: {
    frostedBox: {
      borderRadius: "16px",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      background: FROSTED_BACKGROUND,
      border: FROSTED_BORDER,
      boxShadow: "0 4px 18px rgba(15, 23, 42, 0.08)",
    },
  },

  shadows: [
    "none",
    "0 1px 3px rgba(0,0,0,0.08)",
    "0 4px 12px rgba(0,0,0,0.1)",
    "0 8px 24px rgba(0,0,0,0.15)",
  ],

  transitions: {
    create: () => `all 0.3s cubic-bezier(0.4, 0, 0.2, 1)`,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: OFF_WHITE,
        },
        a: {
          color: BROWN,
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: BROWN,
          color: "#FFFFFF",
          boxShadow: "none",
          border: "none",
          "&:hover": {
            backgroundColor: BROWN,
            boxShadow: "none",
            border: "none",
          },
        }
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight:48,
          height:48,
          "@media (min-width:600px)": { minHeight: 48 },
        }
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 16,
          paddingBlock: 10,
        },
        containedPrimary: {
          backgroundColor: GOLD,
          color: BROWN,
          "&:hover": {
            backgroundColor: "#D6C08A", // slightly lighter gold
          },
        },
        outlinedPrimary: {
          borderColor: GOLD,
          color: GOLD,
          "&:hover": {
            borderColor: "#D6C08A",
            backgroundColor: "rgba(201, 176, 123, 0.10)",
          },
        },
        textPrimary: {
          color: GOLD,
          "&:hover": {
            backgroundColor: "rgba(201, 176, 123, 0.10)",
          },
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: "inherit",
          textDecorationColor: "rgba(255,255,255,0.40)",
          "&:hover": {
            color: GOLD,
            textDecorationColor: GOLD,
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: FROSTED_BACKGROUND,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: FROSTED_BORDER,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          transition: "all 0.3s ease",
          "&:not(.MuiAppBar-root):not(.MuiDialog-paper):not(.MuiDrawer-paper):hover": {
            boxShadow: "0 8px 22px rgba(0,0,0,0.15)",
            background: "rgba(255,255,255,0.8)",
          },
          "&.MuiDialog-paper:hover, &.MuiDrawer-paper:hover": {
            background: "#FFFFFF",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: "1px solid rgba(34, 35, 37, 0.08)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          background: FROSTED_BACKGROUND,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
            background: "rgba(255,255,255,0.85)",
          },
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          background: FROSTED_BACKGROUND,
          backdropFilter: "blur(12px)",
          borderRadius: 16,
          border: FROSTED_BORDER,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: "1px solid rgba(34, 35, 37, 0.10)",
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "#FFFFFF",
          backgroundImage: "none",
          "&:hover": {
            backgroundColor: "#FFFFFF",
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#FFFFFF",
          backgroundImage: "none",
          "&:hover": {
            backgroundColor: "#FFFFFF",
          },
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          marginInline: 8,
          marginBlock: 4,
          "&:hover": {
            backgroundColor: "rgba(201, 176, 123, 0.12)",
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: GOLD,
          height: 3,
          borderRadius: 3,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255,255,255,0.12)",
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: "rgba(34, 35, 37, 0.18)",
        },
        root: {
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(34, 35, 37, 0.30)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: GOLD,
            borderWidth: 2,
          },
        },
      },
    },

    // Dark nav chip-like look
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
        outlined: {
          borderColor: BORDER,
        },
      },
    },
  },
});

export default theme;
